import type { Ctx } from '@milkdown/kit/ctx';
import { editorViewCtx } from '@milkdown/kit/core';
import { renderToStaticMarkup } from 'react-dom/server';
import { Info, Warning, Star } from '@phosphor-icons/react';

import { noticeNode, type NoticeStyle } from './notice';
import { insertBlockAtCursor } from '../../plugins/utils';

type NoticeSlashMenuItemProps = {
  style: NoticeStyle;
};

const NOTICE_CONFIG: Record<NoticeStyle, { label: string; icon: string }> = {
  info: {
    label: 'Info callout',
    icon: renderToStaticMarkup(<Info size={32} weight="fill" />),
  },
  warning: {
    label: 'Warning callout',
    icon: renderToStaticMarkup(<Warning size={32} weight="fill" />),
  },
  tip: {
    label: 'Tip callout',
    icon: renderToStaticMarkup(<Star size={32} weight="fill" />),
  },
};

/**
 * Creates a notice block node with an empty paragraph inside
 */
function createNoticeBlock(ctx: Ctx, style: NoticeStyle) {
  const view = ctx.get(editorViewCtx);
  const { schema } = view.state;
  const noticeType = noticeNode.type(ctx);
  const paragraphType = schema.nodes.paragraph;

  return noticeType.create({ style }, paragraphType.create());
}

/**
 * Creates a slash menu item for inserting a notice block
 */
const noticeSlashMenuItem = ({ style }: NoticeSlashMenuItemProps) => {
  const config = NOTICE_CONFIG[style];

  return {
    label: config.label,
    icon: config.icon,
    onRun: (ctx: Ctx) => {
      const blockNode = createNoticeBlock(ctx, style);
      insertBlockAtCursor(ctx, blockNode);
    },
  };
};

/**
 * Pre-configured slash menu items for each notice type
 */
const infoSlashMenuItem = () => noticeSlashMenuItem({ style: 'info' });
const warningSlashMenuItem = () => noticeSlashMenuItem({ style: 'warning' });
const tipSlashMenuItem = () => noticeSlashMenuItem({ style: 'tip' });

export const SlashMenuItems = {
  info: infoSlashMenuItem,
  warning: warningSlashMenuItem,
  tip: tipSlashMenuItem,
};
